import { createFileRoute } from "@tanstack/react-router";
import { QrCode, Smartphone } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import ArModelViewerLauncher from "@/features/viewer/ar/ArModelViewerLauncher";
import type { ArParkedUrls } from "@/features/viewer/ar/arDelivery";
import { loadModelViewer } from "@/features/viewer/ar/arLaunch";
import {
  type ArLaunchPlatform,
  arLaunchPlatform,
  fetchableHttpUrl,
  resolveArLaunch,
} from "@/features/viewer/ar/arLaunchRouting";
import { i18n } from "@/lib/i18n";
import { appShellTitle } from "@/lib/seo/head";
import { cn } from "@/lib/utils";

// The AR-launch page (ADR-0035 §7, issue #383) — the far end of the desktop QR
// handoff. The phone scans the desktop's QR (or opens the copied link) and lands
// HERE: a minimal, PUBLIC, NON-session-gated page that loads NO Stage / preview / 3D
// scene. It reads the parked `glbUrl` / `usdzUrl` off the query (self-authenticating
// signed URLs), detects the platform, and opens the right OS AR engine — iOS Quick
// Look, Android Scene Viewer — off the HOSTED asset. No login, no session, no
// `/preview/$jobId` reopen (which would 403 on the manifest ownership gate).
//
// The route is classified `noindex` (seo/indexPolicy) — so it carries an
// `X-Robots-Tag: noindex` header AND an SPA-fallback rewrite (the phone loads it fresh
// by URL, so it MUST resolve to the shell), and it is kept out of the sitemap
// (defence-in-depth: the query carries signed asset URLs).

/** The query the QR encodes: the parked delivery URLs (`buildArLaunchUrl`). */
interface ArLaunchSearch {
  glbUrl?: string;
  usdzUrl?: string;
}

function asUrlParam(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

// Detect the OS family on the client. The page is never prerendered (noindex), so this
// only runs client-side where `navigator` exists; the SSR guard is defensive.
function detectPlatform(): ArLaunchPlatform {
  if (typeof navigator === "undefined") return "other";
  return arLaunchPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0);
}

// The rendered screen, split out from the route so the platform × asset matrix is
// unit-testable with an injected platform (no router, no real `navigator`). Given a
// resolvable platform + asset it shows the one-tap "Zobacz w AR" launch (AR needs a
// genuine user gesture, so it is behind the button); otherwise it shows the reason —
// "open this on your phone" (a desktop opened the link) or "the link expired,
// re-generate on desktop" (an absent / expired short-TTL asset).
export function ArLaunchScreen({
  platform,
  glbUrl,
  usdzUrl,
}: ArLaunchSearch & {
  platform: ArLaunchPlatform;
}) {
  const { t } = useTranslation("viewer");
  const [status, setStatus] = useState<"idle" | "launching" | "error">("idle");
  // When set, the shared launcher mounts and hands the parked asset to the OS AR
  // engine; cleared on success (the OS took over) or failure.
  const [launch, setLaunch] = useState<ArParkedUrls | null>(null);

  const fail = useCallback(() => {
    setLaunch(null);
    setStatus("error");
  }, []);
  const handleLaunched = useCallback(() => {
    setLaunch(null);
    setStatus("idle");
  }, []);

  // Scheme-guard BOTH query URLs once (ADR-0035 §3): a crafted non-http(s) param
  // (`javascript:` / `data:` / `blob:`) collapses to `undefined` here, so it drives
  // neither the gating decision below NOR the `<model-viewer>` `src` / `ios-src` it
  // would otherwise reach on the launch tap. The guard must hold on the pass-through
  // field too, not only the platform's gating field — the invariant `arLaunchRouting`
  // documents ("never … downstream"). Not exploitable today, but the code honors it.
  const glb = fetchableHttpUrl(glbUrl);
  const usdz = fetchableHttpUrl(usdzUrl);

  const resolution = resolveArLaunch(platform, { glbUrl: glb, usdzUrl: usdz });
  const ready = resolution.kind === "quick-look" || resolution.kind === "scene-viewer";

  const onLaunch = async () => {
    if (status === "launching") return;
    setStatus("launching");
    try {
      // Lazy-load `<model-viewer>` (its own three.js) only now, on the launch tap —
      // it never enters this minimal page's initial load. Both VALIDATED parked URLs
      // are handed over; model-viewer picks per platform (GLB `src` for Scene Viewer /
      // WebXR, USDZ `ios-src` for Quick Look).
      await loadModelViewer();
      setLaunch({ glbUrl: glb, usdzUrl: usdz });
    } catch {
      setStatus("error");
    }
  };

  const bodyText =
    resolution.kind === "unsupported"
      ? t("ar.launch.unsupported")
      : resolution.kind === "missing-asset"
        ? t("ar.launch.missing")
        : t("ar.launch.lead");

  const Icon = ready ? Smartphone : resolution.kind === "unsupported" ? Smartphone : QrCode;

  return (
    <section
      className="mx-auto flex min-h-[60svh] max-w-sm flex-col items-center justify-center gap-5 py-10 text-center"
      data-testid={ready ? "ar-launch-ready" : `ar-launch-${resolution.kind}`}
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex size-14 items-center justify-center rounded-full",
          ready ? "bg-primary/12 text-primary" : "bg-foreground/[0.06] text-foreground/70",
        )}
      >
        <Icon className="size-7" />
      </span>
      <h1 className="font-display text-xl font-semibold tracking-tight text-foreground">
        {t("ar.launch.title")}
      </h1>
      <p className="text-sm text-muted-foreground">{bodyText}</p>

      {ready ? (
        status === "error" ? (
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground" data-testid="ar-launch-error">
              {t("ar.launch.error")}
            </p>
            <Button type="button" className="rounded-full px-8" onClick={() => void onLaunch()}>
              {t("ar.launch.retry")}
            </Button>
          </div>
        ) : (
          // The page's single primary action (the "One Warm Thread" coral is right
          // here — this whole page exists only to launch AR; there is no "Zamów" to
          // outrank it).
          <Button
            type="button"
            className="min-h-[3.25rem] rounded-full px-10 text-sm font-semibold shadow-md hover:shadow-lg"
            disabled={status === "launching"}
            aria-busy={status === "launching"}
            onClick={() => void onLaunch()}
            data-testid="ar-launch-cta"
          >
            {status === "launching" ? t("ar.launch.launching") : t("ar.launch.cta")}
          </Button>
        )
      ) : null}

      {launch ? (
        <ArModelViewerLauncher urls={launch} onLaunched={handleLaunched} onError={fail} />
      ) : null}
    </section>
  );
}

function ArLaunchRoute() {
  const { glbUrl, usdzUrl } = Route.useSearch();
  // Computed once on mount — a device's OS family doesn't change mid-visit.
  const [platform] = useState(detectPlatform);
  return <ArLaunchScreen platform={platform} glbUrl={glbUrl} usdzUrl={usdzUrl} />;
}

export const Route = createFileRoute("/ar-launch")({
  validateSearch: (search: Record<string, unknown>): ArLaunchSearch => ({
    glbUrl: asUrlParam(search.glbUrl),
    usdzUrl: asUrlParam(search.usdzUrl),
  }),
  head: () => ({ meta: [{ title: appShellTitle(i18n.t("ar.launch.title", { ns: "viewer" })) }] }),
  component: ArLaunchRoute,
});
